import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faRedo,
  faUserCircle,
  faCaretDown,
  faUser,
  faSignOutAlt,
  faCrown,
  faArrowUp,
  faArrowDown,
  faMinus,
  faInfoCircle,
  faSyncAlt,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import '../scss/BrakeMonitoring.scss';
import BaseURL from '../assets/contants/BaseURL';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BrakeMonitoring = () => {
  const navigate = useNavigate();

  // State for filters
  const [selectedCrane, setSelectedCrane] = useState('all');
  const [selectedBrakeType, setSelectedBrakeType] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('today');

  // State for applied filters
  const [appliedCrane, setAppliedCrane] = useState('all');
  const [appliedBrakeType, setAppliedBrakeType] = useState('all');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);

  // Brake data state
  const brakeData = {
    timestamps: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    activations: [120, 145, 98, 167, 201, 75, 45],
    wearLevels: [15, 18, 22, 25, 28, 30, 32],
    vfdFaults: [0, 1, 0, 2, 0, 1, 0]
  }

  // Stats state
  const stats = {
    totalActivations: 12487,
    avgWearRate: 2.3,
    vfdFaults: 4,
    predictedLifespan: 342
  }

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  // Table data state
  const [tableData, setTableData] = useState([]);

  // Chart refs
  const activationChartRef = useRef();
  const wearChartRef = useRef();
  const faultChartRef = useRef();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getStatusClass = (wear, vfdFaults) => {
    if (wear >= 40) return 'danger';
    if (wear >= 30) return 'warning';
    if (vfdFaults > 0) return 'fault';
    return 'normal';
  };

  const getStatusText = (wear, vfdFaults) => {
    if (wear >= 40) return 'Critical';
    if (wear >= 30) return 'Warning';
    if (vfdFaults > 0) return 'Fault';
    return 'Normal';
  };

  const getWearBarClass = (wear) => {
    if (wear >= 40) return 'high';
    if (wear >= 30) return 'medium';
    return 'low';
  };

  // Filter table data
  const filterTableData = useCallback(() => {
    const data = [
      { timestamp: '2023-06-15 08:23:45', crane: 'CRN-001', type: 'Hoist Brake', activations: 120, shoeStatus: 'Good', wear: 15, vfdFaults: 0, lifetime: '12,487', status: 'normal' },
      { timestamp: '2023-06-15 08:45:12', crane: 'CRN-002', type: 'Trolley Brake', activations: 85, shoeStatus: 'Fair', wear: 28, vfdFaults: 1, lifetime: '8,742', status: 'warning' },
      { timestamp: '2023-06-15 09:12:33', crane: 'CRN-001', type: 'Bridge Brake', activations: 67, shoeStatus: 'Good', wear: 18, vfdFaults: 0, lifetime: '10,325', status: 'normal' },
      { timestamp: '2023-06-15 10:05:27', crane: 'CRN-004', type: 'Hoist Brake', activations: 201, shoeStatus: 'Poor', wear: 42, vfdFaults: 2, lifetime: '15,892', status: 'danger' },
      { timestamp: '2023-06-15 10:45:52', crane: 'CRN-002', type: 'Trolley Brake', activations: 63, shoeStatus: 'Good', wear: 22, vfdFaults: 0, lifetime: '9,156', status: 'normal' },
      { timestamp: '2023-06-15 11:15:06', crane: 'CRN-005', type: 'Hoist Brake', activations: 178, shoeStatus: 'Fair', wear: 35, vfdFaults: 1, lifetime: '14,237', status: 'warning' },
      { timestamp: '2023-06-15 11:52:41', crane: 'CRN-001', type: 'Trolley Brake', activations: 92, shoeStatus: 'Good', wear: 20, vfdFaults: 0, lifetime: '11,489', status: 'normal' },
      { timestamp: '2023-06-15 12:30:15', crane: 'CRN-003', type: 'Bridge Brake', activations: 55, shoeStatus: 'Excellent', wear: 12, vfdFaults: 0, lifetime: '7,845', status: 'normal' },
      { timestamp: '2023-06-15 13:18:37', crane: 'CRN-004', type: 'Trolley Brake', activations: 113, shoeStatus: 'Fair', wear: 31, vfdFaults: 1, lifetime: '13,672', status: 'warning' },
      { timestamp: '2023-06-15 14:05:22', crane: 'CRN-002', type: 'Hoist Brake', activations: 145, shoeStatus: 'Good', wear: 25, vfdFaults: 0, lifetime: '12,954', status: 'normal' }
    ];
    
    let filtered = [...data];

    if (appliedCrane !== 'all') {
      filtered = filtered.filter(item => item.crane === appliedCrane);
    }

    if (appliedBrakeType !== 'all') {
      filtered = filtered.filter(item => item.type.toLowerCase().includes(appliedBrakeType.toLowerCase()));
    }

    setTableData(filtered);
  }, [appliedCrane, appliedBrakeType]);

  // Apply filters
  const applyFilters = () => {
    setAppliedCrane(selectedCrane);
    setAppliedBrakeType(selectedBrakeType);
    showNotification('Filters applied successfully', 'success');
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedCrane('all');
    setSelectedBrakeType('all');
    setSelectedDateRange('today');
    setAppliedCrane('all');
    setAppliedBrakeType('all');
    showNotification('Filters reset to default', 'success');
  };

  const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    resizeDelay: 0,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  const activationChartOptions = {
    ...baseChartOptions,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Activation Count' },
        ticks: { stepSize: 50 }
      },
      x: { title: { display: true, text: 'Day of Week' } }
    }
  };

  const activationChartData = {
    labels: brakeData.timestamps,
    datasets: [{
      label: 'Brake Activations',
      data: brakeData.activations,
      backgroundColor: 'rgba(52, 152, 219, 0.7)',
      borderColor: 'rgba(52, 152, 219, 1)',
      borderWidth: 1
    }]
  };

  const wearChartOptions = {
    ...baseChartOptions,
    scales: {
      y: {
        beginAtZero: true,
        max: 50,
        title: { display: true, text: 'Wear Percentage' },
        ticks: { stepSize: 10 }
      },
      x: { title: { display: true, text: 'Day of Week' } }
    },
    elements: {
      line: { tension: 0.4 }
    }
  };

  // Fault Chart Options
  const faultChartOptions = {
    ...baseChartOptions,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Fault Count' },
        ticks: { stepSize: 1, precision: 0 }
      },
      x: { title: { display: true, text: 'Day of Week' } }
    }
  };

  const wearChartData = {
    labels: brakeData.timestamps,
    datasets: [{
      label: 'Wear Level (%)',
      data: brakeData.wearLevels,
      borderColor: '#e74c3c',
      backgroundColor: 'rgba(231, 76, 60, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const faultChartData = {
    labels: brakeData.timestamps,
    datasets: [{
      label: 'VFD Faults',
      data: brakeData.vfdFaults,
      backgroundColor: 'rgba(243, 156, 18, 0.7)',
      borderColor: 'rgba(243, 156, 18, 1)',
      borderWidth: 1
    }]
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('isLoggedIn');
      sessionStorage.clear();
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };


  // Update table when filters change
  useEffect(() => {
    filterTableData();
  }, [appliedCrane, appliedBrakeType, filterTableData]);

  return (
    <div className="brake-monitoring-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
            <div className="account-dropdown">
              <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {orgLogo ? (
                                  <img
                                    src={orgLogo}
                                    alt="Organization Logo"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '2px solid #4361ee'
                                    }}
                                    onError={() => setOrgLogo(null)}
                                  />
                                ) : (
                                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                )}
                <span>{sessionStorage.getItem('username') || 'User'}</span>
                <FontAwesomeIcon icon={faCaretDown} />
              </div>
              {isDropdownOpen && (
                <div className="account-menu show">
                  <div className="account-menu-item plan-info">
                    <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                    <span>Basic Plan</span>
                  </div>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faUser} /> Profile
                  </button>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                  </button>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="page-title">
          <h1>Brake Monitoring</h1>
          <p>Real-time brake system status and performance metrics</p>
        </div>

        {/* Filter Controls */}
        <div className="log-controls">
          <div className="filter-group">
            <label className="filter-label">Crane</label>
            <select
              className="filter-select"
              value={selectedCrane}
              onChange={(e) => setSelectedCrane(e.target.value)}
            >
              <option value="all">All Cranes</option>
              <option value="CRN-001">Gantry Crane #1 (CRN-001)</option>
              <option value="CRN-002">Overhead Crane #2 (CRN-002)</option>
              <option value="CRN-003">Jib Crane #3 (CRN-003)</option>
              <option value="CRN-004">Bridge Crane #4 (CRN-004)</option>
              <option value="CRN-005">Gantry Crane #5 (CRN-005)</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Brake Type</label>
            <select
              className="filter-select"
              value={selectedBrakeType}
              onChange={(e) => setSelectedBrakeType(e.target.value)}
            >
              <option value="all">All Brakes</option>
              <option value="hoist">Hoist Brake</option>
              <option value="trolley">Trolley Brake</option>
              <option value="bridge">Bridge Brake</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Date Range</label>
            <select
              className="filter-select"
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="action-buttons">
            <button className="btn btn-primary" onClick={applyFilters}>
              <FontAwesomeIcon icon={faFilter} /> Apply Filters
            </button>
            <button className="btn btn-secondary" onClick={resetFilters}>
              <FontAwesomeIcon icon={faRedo} /> Reset
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-title">Total Brake Activations</div>
            <div className="stat-valueb">{stats.totalActivations.toLocaleString()}</div>
            <div className="stat-trend trend-up">
              <FontAwesomeIcon icon={faArrowUp} /> 3.2% from last week
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-title">Average Wear Rate</div>
            <div className="stat-valueb">{stats.avgWearRate}%</div>
            <div className="stat-trend trend-down">
              <FontAwesomeIcon icon={faArrowDown} /> 0.5% from last month
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-title">VFD Faults</div>
            <div className="stat-valueb">{stats.vfdFaults}</div>
            <div className="stat-trend trend-neutral">
              <FontAwesomeIcon icon={faMinus} /> No change from last week
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-title">Predicted Lifespan</div>
            <div className="stat-valueb">{stats.predictedLifespan} days</div>
            <div className="stat-trend trend-down">
              <FontAwesomeIcon icon={faArrowDown} /> 12 days less than last month
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="chart-row">
          <div className="chart-containere">
            <div className="chart-title">Brake Activation Count</div>
            <div className="chart-wrapper">
              <Bar ref={activationChartRef} data={activationChartData} options={activationChartOptions} />
            </div>
          </div>

          <div className="chart-containere">
            <div className="chart-title">Brake Wear Status</div>
            <div className="chart-wrapper">
              <Line ref={wearChartRef} data={wearChartData} options={wearChartOptions} />
            </div>
          </div>

          <div className="chart-containere">
            <div className="chart-title">VFD Fault History</div>
            <div className="chart-wrapper">
              <Bar ref={faultChartRef} data={faultChartData} options={faultChartOptions} />
            </div>
          </div>
        </div>

        {/* Brake Log Table */}
        <div className="log-table-container">
          <table className="log-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Crane ID</th>
                <th>Brake Type</th>
                <th>Activation Count</th>
                <th>Shoe Status</th>
                <th>Wear Status</th>
                <th>VFD Faults</th>
                <th>Lifetime Stats</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '2rem', marginBottom: '10px' }} />
                    <p>No data found for the selected filters</p>
                  </td>
                </tr>
              ) : (
                tableData.map((item, index) => {
                  const statusClass = getStatusClass(item.wear, item.vfdFaults);
                  const statusText = getStatusText(item.wear, item.vfdFaults);
                  const wearBarClass = getWearBarClass(item.wear);

                  return (
                    <tr key={index}>
                      <td>{item.timestamp}</td>
                      <td>{item.crane}</td>
                      <td>{item.type}</td>
                      <td>{item.activations}</td>
                      <td>{item.shoeStatus}</td>
                      <td>
                        {item.wear}%
                        <div className="wear-progress">
                          <div className={`wear-progress-bar wear-${wearBarClass}`} style={{ width: `${item.wear}%` }}></div>
                        </div>
                      </td>
                      <td>{item.vfdFaults}</td>
                      <td>{item.lifetime}</td>
                      <td>
                        <span className={`brake-status status-${statusClass}`}>{statusText}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default BrakeMonitoring;